import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { ArrowUpRight, MapPin, ExternalLink } from "lucide-react";

import { getPublicCityHub, type SectionKey, type PublicHubEntry } from "@/lib/city-hub.functions";
import { SmartImage } from "@/components/ui/SmartImage";

const SECTION_LABELS: Record<SectionKey, string> = {
  essentials: "City Essentials",
  outdoor: "Outdoor & Activities",
  highlights_events: "Highlights & Events",
};

export const hubQuery = (slug: string) =>
  queryOptions({
    queryKey: ["public-city-hub", slug],
    queryFn: () => getPublicCityHub({ data: { slug } }),
  });

export const Route = createFileRoute("/city-hub/$slug")({
  loader: async ({ params, context }) => {
    const hub = await context.queryClient.ensureQueryData(hubQuery(params.slug));
    if (!hub) throw notFound();
    return null;
  },
  head: ({ params, loaderData }) => {
    // loaderData is null when found; undefined when notFound() was thrown.
    if (loaderData === undefined) {
      return { meta: [{ title: "City not found — what Finland" }, { name: "robots", content: "noindex" }] };
    }
    const name = params.slug.charAt(0).toUpperCase() + params.slug.slice(1);
    const url = `https://whatfinland.com/city-hub/${params.slug}`;
    const title = `${name} City Hub — what Finland`;
    const desc = `Everything you need to know about ${name}: transit, parks, events, and local highlights curated by the ${name} team.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { rel: "canonical", content: url },
      ],
    };
  },
  component: PublicCityHub,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="text-center">
        <h1 className="text-2xl font-heading">City hub not available</h1>
        <p className="mt-2 text-slate-500">This city hasn't published a hub yet.</p>
        <Link to="/cities" className="mt-6 inline-block text-indigo-600 underline">Browse all cities</Link>
      </div>
    </div>
  ),
});

function PublicCityHub() {
  const { slug } = Route.useParams();
  const { data: hub } = useSuspenseQuery(hubQuery(slug));
  if (!hub) return null;

  const brand = hub.branding;
  const accent = brand?.brand_color || "#4f46e5";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section
        className="relative overflow-hidden text-white"
        style={{
          background: brand?.banner_url
            ? undefined
            : `linear-gradient(135deg, ${accent}, ${accent}dd 60%, #0f172a)`,
        }}
      >
        {brand?.banner_url && (
          <>
            <img
              src={brand.banner_url}
              alt={hub.city?.name ?? ""}
              className="absolute inset-0 h-full w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/25" />
          </>
        )}
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          {brand?.logo_url && (
            <img src={brand.logo_url} alt="" className="h-12 mb-4 object-contain" />
          )}
          <p className="text-xs uppercase tracking-[0.2em] opacity-80">City Hub</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-heading">{hub.city?.name}</h1>
          {brand?.tagline && (
            <p className="mt-3 max-w-2xl text-lg opacity-90">{brand.tagline}</p>
          )}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-10 space-y-10">
        {(["essentials", "outdoor", "highlights_events"] as SectionKey[]).map((key) => {
          const section = hub.sections.find((s) => s.section_key === key);
          if (!section) return null;
          const entries = hub.entries.filter((e) => e.section_id === section.id);
          if (entries.length === 0) return null;
          return (
            <section key={key}>
              <div className="mb-4 flex items-end justify-between">
                <div>
                  <h2 className="text-2xl font-heading text-slate-900">
                    {SECTION_LABELS[key] ?? section.title}
                  </h2>
                  {section.subtitle && (
                    <p className="mt-1 text-sm text-slate-500">{section.subtitle}</p>
                  )}
                </div>
                <span className="text-xs text-slate-400">{entries.length} items</span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {entries.map((entry) => <HubEntryCard key={entry.id} entry={entry} accent={accent} />)}
              </div>
            </section>
          );
        })}

        {hub.entries.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
            The city team hasn't published hub content yet. Check back soon.
          </div>
        )}
      </div>
    </div>
  );
}

function HubEntryCard({ entry, accent }: { entry: PublicHubEntry; accent: string }) {
  const image = entry.image_url || entry.place?.image_url || entry.event?.image_url || null;
  const title = entry.title || entry.place?.name || entry.event?.title || "Untitled";
  const category = entry.place?.category ?? null;

  const inner = (
    <>
      {image && (
        <div className="relative aspect-[4/3] overflow-hidden">
          <SmartImage src={image} alt={title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
      )}
      <div className="p-4 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-slate-900 text-base leading-tight">{title}</h3>
          <ExternalLink className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
        </div>
        {entry.description && <p className="text-sm text-slate-600 line-clamp-3">{entry.description}</p>}
        {entry.address && (
          <p className="text-xs text-slate-500 inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {entry.address}
          </p>
        )}
        {category && (
          <span
            className="inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
            style={{ background: `${accent}20`, color: accent }}
          >
            {category}
          </span>
        )}
        {entry.curator_note && (
          <p className="mt-2 text-xs text-slate-500 border-l-2 pl-2" style={{ borderColor: accent }}>
            {entry.curator_note}
          </p>
        )}
      </div>
    </>
  );

  const cls = "group block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition-shadow";

  if (entry.place?.id) {
    return (
      <Link to="/place/$id" params={{ id: entry.place.id }} className={cls}>
        {inner}
      </Link>
    );
  }
  if (entry.url) {
    return (
      <a href={entry.url} target="_blank" rel="noreferrer" className={cls}>
        {inner}
      </a>
    );
  }
  return <div className={cls}>{inner}</div>;
}
